// Master-only: per-install branding + terminology overrides.
//
// Covers Stitch v2 designs `site_setup_branding_terminology` and
// `site_setup_branding_terminology_axn_pro_access` — the AxN-Pro variant
// is the same page rendered with the operator's saved brand color via
// useBranding(); not a separate component.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import {
  type Branding,
  type PutBrandingBody,
  DEFAULT_BRANDING,
} from '@ng-unifi/domain';
import { apiClient, ApiError } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';
import { useBranding } from '../lib/branding.js';

const TERM_FIELDS: ReadonlyArray<{
  key: 'resident_label' | 'employee_label' | 'building_label';
  label: string;
  fallback: string;
}> = [
  { key: 'resident_label', label: 'Resident label', fallback: 'Residents' },
  { key: 'employee_label', label: 'Employee label', fallback: 'Employees' },
  { key: 'building_label', label: 'Building label', fallback: 'Buildings' },
];

export function SetupBrandingPage() {
  const { state } = useAuth();
  const { refresh: refreshBranding } = useBranding();
  const navigate = useNavigate();
  const [form, setForm] = useState<Branding>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const isMaster = state.status === 'authed' && state.user.role === 'master_admin';

  useEffect(() => {
    if (state.status === 'authed' && !isMaster) {
      navigate('/', { replace: true });
    }
  }, [state, isMaster, navigate]);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    apiClient
      .get<{ data: Branding }>('/api/v1/admin/branding', { skipTenantScope: true })
      .then((res) => setForm(res.data))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load branding'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isMaster) reload();
  }, [isMaster, reload]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSavedAt(null);
    try {
      const payload: PutBrandingBody = {
        logo_url: form.logo_url,
        brand_color: form.brand_color,
        terminology: form.terminology,
      };
      const res = await apiClient.put<{ data: Branding }>(
        '/api/v1/admin/branding',
        payload,
        { skipTenantScope: true },
      );
      setForm(res.data);
      setSavedAt(new Date());
      await refreshBranding();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (state.status !== 'authed' || !isMaster || loading) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Branding &amp; terminology</h1>
        <p className="text-on-surface-variant">
          Customize the portal logo, accent color, and the labels operators see for
          residents, employees, and buildings. Changes apply immediately to every
          signed-in session on next page load.
        </p>
      </header>

      <Card>
        <CardBody>
          <form onSubmit={onSubmit} className="grid gap-6">
            <div className="grid gap-2">
              <label htmlFor="brand-logo-url" className="font-medium">
                Logo URL (https only)
              </label>
              <input
                id="brand-logo-url"
                type="url"
                value={form.logo_url ?? ''}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value || null })}
                placeholder="https://cdn.example.com/logo.png"
                pattern="^https://.+"
                className="rounded border border-outline px-3 py-2"
              />
              {form.logo_url ? (
                <img
                  src={form.logo_url}
                  alt="Logo preview"
                  className="h-12 w-auto max-w-[12rem] object-contain border border-outline-variant rounded p-1"
                />
              ) : null}
            </div>

            <div className="grid gap-2">
              <label htmlFor="brand-color" className="font-medium">
                Brand color (hex, e.g. #005FCC)
              </label>
              <div className="flex items-center gap-3">
                <input
                  id="brand-color"
                  type="text"
                  value={form.brand_color ?? ''}
                  onChange={(e) => setForm({ ...form, brand_color: e.target.value || null })}
                  placeholder="#005FCC"
                  pattern="^#[0-9a-fA-F]{6}$"
                  className="rounded border border-outline px-3 py-2 w-40 font-mono"
                />
                {form.brand_color ? (
                  <span
                    aria-label={`Preview ${form.brand_color}`}
                    className="h-8 w-12 rounded border border-outline"
                    style={{ background: form.brand_color }}
                  />
                ) : null}
              </div>
            </div>

            <fieldset className="grid gap-3 border border-outline-variant rounded p-4">
              <legend className="px-2 font-medium">Terminology overrides</legend>
              <p className="text-sm text-on-surface-variant">
                Override the labels operators see throughout the portal. Leave a field
                blank to keep the default term.
              </p>
              {TERM_FIELDS.map(({ key, label, fallback }) => (
                <div key={key} className="grid gap-1">
                  <label htmlFor={`term-${key}`} className="text-sm">
                    {label}
                  </label>
                  <input
                    id={`term-${key}`}
                    type="text"
                    // `key` is a typed-literal from TERM_FIELDS, not user input.
                    // eslint-disable-next-line security/detect-object-injection
                    value={form.terminology[key] ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        terminology: {
                          ...form.terminology,
                          [key]: e.target.value || null,
                        },
                      })
                    }
                    maxLength={40}
                    placeholder={fallback}
                    className="rounded border border-outline px-3 py-2"
                  />
                </div>
              ))}
            </fieldset>

            {error ? (
              <p role="alert" className="text-error">
                <Icon name="error" /> {error}
              </p>
            ) : null}
            {savedAt ? (
              <p className="text-primary">
                <Icon name="check_circle" /> Saved at {savedAt.toLocaleTimeString()}
              </p>
            ) : null}

            <div className="flex gap-2">
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save branding'}
              </Button>
              <Button type="button" variant="ghost" onClick={reload} disabled={saving}>
                Reset
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
